<template>
  <div class="relative h-full min-h-0 w-full bg-black">
    <video
      ref="videoRef"
      class="absolute inset-0 h-full w-full object-contain"
      playsinline
      muted
      crossorigin="anonymous"
      preload="auto"
      :poster="posterFileUrl ?? undefined"
      @error="handleVideoError"
    />
  </div>
</template>

<script setup lang="ts">
import {
  attachPlaybackEndDetection,
  destroyHlsVideo,
  loadHlsVideo,
  playMutedVideo,
  useHlsVideoCleanup,
  waitForRenderableVideo,
} from '@/hooks/use-hls-video';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps<{
  fileUrl: string;
  posterFileUrl?: string | null;
}>();

const emit = defineEmits<{
  (e: 'ended'): void;
  (e: 'error', payload: { fileUrl: string }): void;
  (e: 'canplay'): void;
  (e: 'playing'): void;
}>();

const videoRef = ref<HTMLVideoElement | null>(null);
const loadedUrl = ref<string | null>(null);

let playbackEndCleanup: (() => void) | null = null;

useHlsVideoCleanup(() => [videoRef.value]);

onBeforeUnmount(() => {
  clearPlaybackEndDetection();
});

function clearPlaybackEndDetection() {
  playbackEndCleanup?.();
  playbackEndCleanup = null;
}

function bindPlaybackEndDetection(videoEl: HTMLVideoElement) {
  clearPlaybackEndDetection();
  playbackEndCleanup = attachPlaybackEndDetection(videoEl, () => {
    void handlePlaybackEnded();
  });
}

function handleVideoError() {
  if (!props.fileUrl) return;
  emit('error', { fileUrl: props.fileUrl });
}

async function playVideo() {
  const videoEl = videoRef.value;
  if (!videoEl) return false;

  const played = await playMutedVideo(videoEl);
  if (!played) {
    emit('error', { fileUrl: props.fileUrl });
    return false;
  }

  try {
    await waitForRenderableVideo(videoEl);
  } catch {
    emit('error', { fileUrl: props.fileUrl });
    return false;
  }

  bindPlaybackEndDetection(videoEl);
  emit('playing');
  return true;
}

async function loadAndPlay(fileUrl: string) {
  if (!fileUrl) return;

  await nextTick();
  const videoEl = videoRef.value;
  if (!videoEl) return;

  if (loadedUrl.value === fileUrl) return;

  clearPlaybackEndDetection();
  try {
    await loadHlsVideo(videoEl, fileUrl, { cached: true });
  } catch {
    loadedUrl.value = null;
    emit('error', { fileUrl });
    return;
  }

  loadedUrl.value = fileUrl;
  emit('canplay');
  await playVideo();
}

function handlePlaybackEnded() {
  emit('ended');
}

watch(
  () => props.fileUrl,
  (fileUrl) => {
    if (!fileUrl) {
      clearPlaybackEndDetection();
      destroyHlsVideo(videoRef.value);
      loadedUrl.value = null;
      return;
    }
    if (loadedUrl.value === fileUrl) return;
    void loadAndPlay(fileUrl);
  },
  { immediate: true },
);
</script>
